// crewbit-admin/src/pages/Money/Recurring.tsx
//
// SPEC: §Money.RecurringTemplates (filed 2026-05-04 during Phase B Session 140
// as gap 3 of 3 before §FinancialsPage.FinalRetire). Thin-wrap of the existing
// <RecurringTab> + <CreateRecurringPanel> via the <useRecurring> envelope.
//
// Top-level admin page for /money/recurring. Composes PageHeader + thin-wraps
// the existing <RecurringTab> via the <useRecurring> envelope; mounts
// <CreateRecurringPanel> as a slide-over with URL search-param deep-link
// (?action=new-recurring) mirroring MoneyInvoicesPage's pattern.
//
// Refresh policy is OWNED by useRecurring (Supabase realtime subscription on
// recurring_invoices via useRealtimeSubscription). This page MUST NOT add
// visibility/window listeners of its own.
//
// <RequiresActiveBusinessGate> is applied at the route boundary in App.tsx;
// the !businessId branch here is defense-in-depth for transitions (mirrors
// Revenue.tsx + Outstanding.tsx + Overview.tsx pattern).

import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Building2, Plus } from 'lucide-react';
import { PageHeader } from '../../components/common/PageHeader';
import { EmptyState } from '../../components/common/EmptyState';
import { Toast } from '../../components/common/Toast';
import { TableSkeleton } from '../../components/common/Skeleton';
import { RecurringTab } from '../../components/money/RecurringTab';
import { CreateRecurringPanel } from '../../components/money/CreateRecurringPanel';
import SettingsGearLink from '../../components/money/settings/SettingsGearLink';
import { useAuthStore } from '../../stores/authStore';
import { useRecurring } from '../../hooks/useRecurring';
import { admin, palette, radii, spacing, typography } from '../../styles/tokens';

const pageStyle: React.CSSProperties = {
  padding: spacing.xl,
  background: admin.bg,
  minHeight: '100vh',
};

const cardStyle: React.CSSProperties = {
  background: palette.white,
  borderRadius: admin.cardRadius,
  border: `1px solid ${admin.border}`,
  overflow: 'hidden',
};

type ToastState = {
  msg: string;
  type: 'success' | 'error' | 'warning' | 'info';
} | null;

export function MoneyRecurringPage() {
  const business = useAuthStore((s) => s.business);
  const businessId = business?.id;
  const recurring = useRecurring(businessId);

  const [searchParams, setSearchParams] = useSearchParams();
  const [panelOpen, setPanelOpen] = useState(false);
  const [toast, setToast] = useState<ToastState>(null);

  useEffect(() => {
    document.title = 'CrewBit · Recurring Invoices';
  }, []);

  useEffect(() => {
    if (searchParams.get('action') === 'new-recurring') {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- one-time URL action consumption (mirrors MoneyInvoicesPage:110-127)
      setPanelOpen(true);
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete('action');
          return next;
        },
        { replace: true },
      );
    }
  }, [searchParams, setSearchParams]);

  const headerActions = (
    <button type="button" onClick={() => setPanelOpen(true)} style={primaryButtonStyle}>
      <Plus size={14} />
      New Recurring Invoice
    </button>
  );

  // Branch 1 — defensive no-business gate. Outer <RequiresActiveBusinessGate>
  // short-circuits at the route boundary; this is a fallback for transitions.
  if (!businessId) {
    return (
      <div style={pageStyle}>
        <PageHeader title="Recurring Invoices" back={{ to: '/money', label: 'Money' }} secondaryActions={<SettingsGearLink />} />
        <EmptyState
          icon={<Building2 size={22} />}
          title="Pick a business"
          subtitle="Switch to an active business from the sidebar to manage recurring invoices."
        />
      </div>
    );
  }

  // Branch 2 — initial load. useRecurring exposes only `loading`.
  if (recurring.loading) {
    return (
      <div style={pageStyle}>
        <PageHeader
          title="Recurring Invoices" back={{ to: '/money', label: 'Money' }}
          primaryAction={headerActions}
          secondaryActions={<SettingsGearLink />}
        />
        <div style={cardStyle}>
          <TableSkeleton rows={3} columns={7} />
        </div>
      </div>
    );
  }

  // Branch 3 — happy path. Thin-wrap RecurringTab with useRecurring envelope.
  // RecurringTab owns the empty-state CTA, table rendering, and per-row
  // toggleActive / toggleAutoSend / generate actions.
  return (
    <div style={pageStyle}>
      <PageHeader
        title="Recurring Invoices" back={{ to: '/money', label: 'Money' }}
        primaryAction={headerActions}
        secondaryActions={<SettingsGearLink />}
      />
      <div style={cardStyle}>
        <RecurringTab
          templates={recurring.templates}
          toggleActive={recurring.toggleActive}
          toggleAutoSend={recurring.toggleAutoSend}
          generateInvoice={recurring.generateInvoice}
          onNewRecurring={() => setPanelOpen(true)}
        />
      </div>

      <CreateRecurringPanel
        isOpen={panelOpen}
        onClose={() => setPanelOpen(false)}
        onCreated={() => {
          setPanelOpen(false);
          recurring.refresh();
          setToast({ msg: 'Recurring template created', type: 'success' });
        }}
        createTemplate={recurring.createTemplate}
      />

      {toast && (
        <Toast
          message={toast.msg}
          type={toast.type}
          isVisible={!!toast}
          onDismiss={() => setToast(null)}
        />
      )}
    </div>
  );
}

const primaryButtonStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  padding: '8px 14px',
  borderRadius: radii.sm,
  border: 'none',
  background: palette.accent,
  color: palette.white,
  fontSize: typography.size.caption,
  fontWeight: typography.weight.semibold,
  fontFamily: 'inherit',
  cursor: 'pointer',
};
